// quoteTotals.js
//
// Unified totals shape for all five quote types — single source of truth
// for what gets persisted into quotes.totals (and quotes_versions.totals via
// saveQuoteVersion). Closes the §10b Phase 1 item 2 architectural gap
// where each page hand-rolled its own totals object, drifted in shape, and
// silently dropped fields between live save and version snapshot (see
// CLAUDE.md §3 history on Bundle/FlexIT save-vs-snapshot mismatches).
//
// Rollout (CLAUDE.md §10b):
//   v3.5.92.0 — utility introduced; Managed IT migrated  ← THIS DEPLOY
//   v3.5.92.1 — Multi-Site migrated
//   v3.5.92.2 — Voice + Bundle + FlexIT migrated
//   v3.5.92.3 — consumer cleanup (QuotesPage / quoteDiff / buildSignatureDocument / ProposalsPage)
//
// Identity rule: the object buildTotals() returns IS what gets persisted.
// No separate "live vs persistable" projection. The page passes this same
// object to BOTH the upsert payload AND saveQuoteVersion — eliminating the
// pre-v3.5.92.x bug class where Bundle persisted 9 fields live but 5 in
// the snapshot, and FlexIT persisted 11 live but 3 in the snapshot.
//
// Type discriminator: the canonical 'managed_it' | 'voice' | 'bundle' |
// 'multisite' | 'flexit' keys from src/lib/quotePermissions.js QUOTE_TYPES
// (same strings used by profiles.allowed_quote_types[] and RLS, set as
// quotes.quote_type since v3.5.91.0).
//
// Conditional-field policy (§5 #5 decision May 10 2026): fields that are
// type-inapplicable are OMITTED rather than set to null/0. Consumers that
// want ITSO read `totals.itsoMRR > 0` — works for both "field absent"
// (undefined → falsy) and "field present but zero" (0 → falsy). Same code
// path, no quote-type branching at the consumer site.
//
// NRC layer (§4/§4a decision May 10 2026): `nrc` is the total customer
// one-time charge; `onboarding` is the LABOR PORTION ONLY (Programming &
// Configuration), which is what the OnboardingIncentive picker discounts.
// `hardwareNRC` / `activationNRC` / `portingNRC` carry the non-labor
// pieces separately. Identity: nrc = onboarding + hardwareNRC +
// activationNRC + portingNRC (modulo rounding). For Managed IT there's no
// hardware/activation/porting, so all three non-labor fields are 0 and
// nrc === onboarding.

import { getBurdenedRate } from './pricing';
import { applyManualDiscounts } from './manualDiscounts';
import { applyOneTimeDiscounts } from './oneTimeDiscounts';

// v3.5.99.7.23 — resolve manual discount lines against a base MRR and return
// the fields every recurring builder persists. Centralized so all four types
// (IT / Voice / Bundle / Multi-Site) carry an identical discount shape in
// totals. The net MRR returned here becomes finalMRR, so impliedGM and
// contractValue (which derive from finalMRR/effectiveMRR) compress
// automatically — no per-type margin handling needed.
function resolveManualDiscounts(baseMRR, manualDiscounts) {
  const md = applyManualDiscounts(baseMRR, manualDiscounts);
  return {
    finalMRR: md.finalMRR,                       // net of manual discounts
    fields: {
      preManualDiscountMRR: md.base,             // MRR before manual discounts
      manualDiscounts:      md.lines,            // [{ type, value, note, amount }]
      manualDiscountTotal:  md.total,            // $/mo removed
    },
  };
}

// v3.7.8 — resolve TARGETED one-time (non-recurring) discount lines against the
// per-category NRC bases and return the fields every builder persists. `bases`
// carries the customer charge for each NRC category BEFORE one-time discounts —
// `onboarding` already net of the onboarding incentive (effectiveOnboarding),
// hardware/activation/porting straight from the engine. netNRC (returned
// separately) becomes the one-time figure that TCV derives from, so contract
// value compresses automatically. Centralized so all four applicable types
// (IT / Voice / Bundle / Multi-Site) carry an identical one-time-discount shape.
function resolveOneTimeDiscountFields(bases, oneTimeDiscounts) {
  const otd = applyOneTimeDiscounts(bases, oneTimeDiscounts);
  return {
    netNRC: otd.finalNRC,                              // customer one-time total, net
    fields: {
      preOneTimeDiscountNRC:     otd.base,             // one-time total before one-time discounts (post incentive)
      oneTimeDiscounts:          otd.lines,            // [{ category, type, value, note, amount }]
      oneTimeDiscountTotal:      otd.total,            // $ removed
      oneTimeDiscountByCategory: otd.byCategory,       // { <key>: { base, discount, net } }
    },
  };
}

// ─── Internal helpers ──────────────────────────────────────────────────────

// Resolve an onboarding incentive into utility-owned fields. Centralizes
// math that was previously inlined at QuotePage.js lines 591–595 (pre-
// v3.5.92.0). Future migrations (Voice, Bundle, Multi-Site) pick this up
// for free — those pages currently store the incentive at the top-level
// onboarding_incentive column but never roll it into totals.
//
// Per §4a: the incentive applies to PROGRAMMING & CONFIGURATION labor only.
// Pages that supply onboardingLabor must have already pre-filtered out
// hardware / porting / activation NRC. For Managed IT, onboardingLabor
// equals result.onboarding (the engine returns labor-only NRC for IT).
function resolveOnboardingIncentive(onboardingLabor, obIncentive) {
  const hasActive = obIncentive
    && obIncentive.mode
    && obIncentive.mode !== 'none'
    && obIncentive.effectiveFee != null;

  if (!hasActive) {
    return {
      effectiveOnboarding: null,
      onboardingDiscountAmount: 0,
      onboardingIncentiveMode: null,
      onboardingIncentivePct: null,
    };
  }

  const effective = Number(obIncentive.effectiveFee);
  const labor     = Number(onboardingLabor) || 0;
  return {
    effectiveOnboarding: effective,
    onboardingDiscountAmount: Math.max(0, labor - effective),
    onboardingIncentiveMode: obIncentive.mode,
    onboardingIncentivePct: obIncentive.discountPct ?? null,
  };
}

// Resolve flex block fields. Returns all-null shape when no block is
// selected — consumers checking `totals.flexBlockMRR > 0` still work
// (null > 0 is false). When a block IS selected, every field is populated.
function resolveFlexBlock(flexBlock, burdenedRate) {
  if (!flexBlock) {
    return {
      flexBlockHours:     null,
      flexBlockMRR:       null,
      flexBlockPrice:     null,
      flexBlockFullPrice: null,
      flexBlockSavings:   null,
      flexBlockDiscount:  null,
      flexLaborCost:      null,
    };
  }
  const hours = Number(flexBlock.hours) || 0;
  return {
    flexBlockHours:     hours,
    flexBlockMRR:       Number(flexBlock.blockPrice) || 0,
    flexBlockPrice:     Number(flexBlock.blockPrice) || 0,
    flexBlockFullPrice: Number(flexBlock.fullPrice)  || 0,
    flexBlockSavings:   Number(flexBlock.savings)    || 0,
    flexBlockDiscount:  flexBlock.discount != null ? Number(flexBlock.discount) : null,
    // Flex labor cost = hours × burdened rate. Engine output (result.totalCost)
    // does NOT include this — it's computed at the page level (QuotePage line
    // 2400 pre-v3.5.92.0). Centralizing here so every type computes it the
    // same way and totalCost in unified totals is consistently flex-inclusive.
    flexLaborCost:      hours * (Number(burdenedRate) || 0),
  };
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Build the unified totals object for any quote type.
 *
 * @param {string} quoteType — canonical key: 'managed_it' | 'voice' |
 *   'bundle' | 'multisite' | 'flexit'
 * @param {object} engineResult — calc engine output:
 *   - managed_it: calcQuote() return
 *   - voice: calcVoice() return                         (v3.5.92.2)
 *   - bundle: { it: calcQuote, voice: calcVoice, ... }  (v3.5.92.2)
 *   - multisite: { locationResults, aggregations }      (v3.5.92.1)
 *   - flexit: { prepayAmount, flexBlock, ... }          (v3.5.92.2)
 * @param {object} context — page-level inputs:
 *   - obIncentive — OnboardingIncentive component state (or null)
 *   - flexBlock — calcFlexBlock() return (or null)
 *   - contractTerm — months
 *   - settings — pricing_settings keyed object
 * @returns {object} unified totals shape per CLAUDE.md §10b. Empty {} when
 *   engineResult is null (pre-config-load); callers can still pass through
 *   to payload.totals without guarding.
 */
export function buildTotals(quoteType, engineResult, context = {}) {
  if (!engineResult) return {};

  switch (quoteType) {
    case 'managed_it': return buildManagedITTotals(engineResult, context);
    case 'multisite':  return buildMultiSiteTotals(engineResult, context);
    case 'voice':      return buildVoiceTotals(engineResult, context);
    case 'bundle':     return buildBundleTotals(engineResult, context);
    case 'flexit':     return buildFlexITTotals(engineResult, context);
    default:
      // Phase 1 item 2 complete as of v3.5.92.2 — all five canonical quote
      // types are now handled. An unknown value here means a caller has
      // synthesized a non-canonical quoteType, which is a bug. Surface it
      // rather than silently persisting a wrong shape.
      throw new Error(
        `quoteTotals.buildTotals: unknown quoteType "${quoteType}". ` +
        `Expected one of: 'managed_it' | 'multisite' | 'voice' | 'bundle' | 'flexit' ` +
        `(canonical keys per src/lib/quotePermissions.js QUOTE_TYPES).`
      );
  }
}

function buildManagedITTotals(result, context) {
  const { obIncentive, flexBlock, contractTerm, settings, manualDiscounts, oneTimeDiscounts } = context;

  // ── Inputs from calc engine ──
  // v3.5.99.7.23: manual discounts apply to the engine's contract MRR. The
  // net becomes finalMRRBase, so everything below (effectiveMRR, GM, TCV)
  // reflects the discount with no further changes.
  const md            = resolveManualDiscounts(Number(result.finalMRR) || 0, manualDiscounts);
  const finalMRRBase  = md.finalMRR;
  const onboarding    = Number(result.onboarding) || 0;  // labor only on IT
  const engineCost    = Number(result.totalCost) || 0;

  // ── Flex block resolution ──
  // Burden rate is the SAME for live display and persistence — getBurdenedRate
  // is settings-driven with the $80 floor enshrined in v3.5.58. Centralizing
  // here means every type's flexLaborCost uses the same rate; pages can't
  // diverge by picking up a different default.
  const burdenedRate  = getBurdenedRate(settings);
  const flex          = resolveFlexBlock(flexBlock, burdenedRate);
  const flexBlockMRR  = flex.flexBlockMRR  || 0;
  const flexLaborCost = flex.flexLaborCost || 0;

  // ── Revenue (§3 Option B decision May 10 2026) ──
  // finalMRR = labor base (excludes flex block MRR) — what reps think of as
  //   "the contract MRR" and what the proposal headline displays.
  // effectiveMRR = labor base + flex block MRR — used for GM math so the
  //   denominator matches reality (the flex block IS billed monthly during
  //   the term it covers, even though it isn't strictly "the recurring
  //   contract MRR").
  const finalMRR     = finalMRRBase;
  const effectiveMRR = finalMRRBase + flexBlockMRR;

  // ── Cost + margin ──
  // totalCost now consistently includes flex labor on top of engine cost.
  // Pre-v3.5.92.0, QuotePage persisted bare result.totalCost (no flex) but
  // computed displayed GM with flex included (line 2125) — meaning the
  // persisted impliedGM differed from what reps saw. After unification,
  // persisted matches displayed.
  const totalCost = engineCost + flexLaborCost;
  const impliedGM = effectiveMRR > 0 ? 1 - totalCost / effectiveMRR : null;

  // ── NRC layer (§4 + §4a decisions May 10 2026) ──
  // Managed IT has no hardware/activation/porting NRC — onboarding IS the
  // full customer one-time charge. The identity nrc = onboarding +
  // hardwareNRC + activationNRC + portingNRC holds trivially: nrc =
  // onboarding + 0 + 0 + 0 = onboarding.
  const incentive = resolveOnboardingIncentive(onboarding, obIncentive);
  const nrc       = onboarding;

  // ── Contract value (TCV) ──
  // Uses effectiveMRR (with flex) and the post-incentive labor figure when
  // an incentive is active — matches what the customer will actually pay.
  // Pre-v3.5.92.0 QuotePage line 624 used bare finalMRR for this; the
  // displayed TCV in the KPI strip (line 2134) used effectiveFinalMRR.
  // Same alignment fix as impliedGM above.
  const effectiveNRC  = incentive.effectiveOnboarding ?? onboarding;
  // v3.7.8: targeted one-time discounts. IT has only the onboarding bucket
  // (no hardware/activation/porting), based on the post-incentive figure.
  const otd           = resolveOneTimeDiscountFields(
    { onboarding: effectiveNRC, hardware: 0, activation: 0, porting: 0 },
    oneTimeDiscounts
  );
  const netNRC        = otd.netNRC;
  const contractValue = effectiveMRR * (Number(contractTerm) || 0) + netNRC;

  return {
    // ── Type discriminator ──
    quoteType: 'managed_it',

    // ── Revenue ──
    finalMRR,
    effectiveMRR,
    contractValue,

    // ── Manual discounts (v3.5.99.7.23) ──
    ...md.fields,

    // ── NRC layer ──
    nrc,
    onboarding,
    effectiveOnboarding: incentive.effectiveOnboarding,
    hardwareNRC:   0,
    activationNRC: 0,
    portingNRC:    0,
    onboardingDiscountAmount: incentive.onboardingDiscountAmount,
    onboardingIncentiveMode:  incentive.onboardingIncentiveMode,
    onboardingIncentivePct:   incentive.onboardingIncentivePct,

    // ── One-time discounts (v3.7.8) ──
    netNRC,
    ...otd.fields,

    // ── Cost + margin ──
    totalCost,
    impliedGM,

    // ── ITSO (present on IT per §5 #5 decision) ──
    itsoMRR:        Number(result.itsoMRR)        || 0,
    itsoLabel:      result.itsoLabel || null,
    itsoUpliftRate: Number(result.itsoUpliftRate) || 0,

    // ── IT-engine internals ──
    itSubtotal: Number(result.itSubtotal) || 0,
    compMult:   Number(result.compMult)   || 1,
    discRate:   Number(result.discRate)   || 0,
    discount:   Number(result.discount)   || 0,  // NEGATIVE — engine convention

    // ── Flex block (all-null when no block selected) ──
    ...flex,
  };
}

// ─── Multi-Site (v3.5.92.1) ───────────────────────────────────────────────
//
// Multi-Site has no single calc-engine return — calcLocationMRR runs per
// location and the page aggregates results across the location list, then
// layers on ITSO, multi-location discount, contract-term discount, addon
// products, flex block, etc. The page already does this math for display
// (the on-page KPI strip and cost breakdown both read these aggregates),
// so this utility takes the pre-aggregated values via the engineResult dict
// and produces the unified shape from them. Page-side math stays exactly as
// it was for runtime display; only the totals-construction-for-persistence
// block is replaced.
//
// engineResult shape expected (everything pre-flex):
//   { finalLaborMRR, addonRevenue, itsoMRR, itsoLabel, itsoUpliftRate,
//     onboarding, multiDiscRate, termDiscRate, totalLocationMRR,
//     locationCount, toolingCost, svcCost, addonCost, includesCost,
//     swapCost, itsoCost }
//
// flexLaborCost is computed inside the utility from flexBlock + burdened
// rate — same as the IT path — so both quote types derive it identically
// and the engineResult.* cost components must EXCLUDE flex labor. This is
// a deliberate inversion from how the page used to compute effectiveCost
// inline (which DID include flexLaborCost). The page extracts the
// flex-exclusive components and passes them through; the utility owns
// the flex-labor addition. Math is identical to the pre-deploy page
// formula (verified by inspection: page used `effectiveCost = tooling +
// svc + addon + flexLabor + includes + swap + itso`; utility computes
// `engineCost = tooling + svc + addon + includes + swap + itso; totalCost
// = engineCost + flexLabor`).

function buildMultiSiteTotals(r, context) {
  const { obIncentive, flexBlock, contractTerm, settings, manualDiscounts, oneTimeDiscounts } = context;

  // ── Flex block resolution ──
  const burdenedRate  = getBurdenedRate(settings);
  const flex          = resolveFlexBlock(flexBlock, burdenedRate);
  const flexBlockMRR  = flex.flexBlockMRR  || 0;
  const flexLaborCost = flex.flexLaborCost || 0;

  // ── Revenue ──
  // finalMRR construction matches MultiSiteQuotePage.js line 350 pre-v3.5.92.1:
  // labor base (post both discounts) + addons + ITSO. Excludes flex block —
  // same Option B convention as IT, locked May 10 2026 (§3 #3).
  // v3.5.99.7.23: manual discounts apply on top of the composed labor+addon+ITSO MRR.
  const finalLaborMRR    = Number(r.finalLaborMRR)    || 0;
  const addonRevenue     = Number(r.addonRevenue)     || 0;
  const itsoMRR          = Number(r.itsoMRR)          || 0;
  const md               = resolveManualDiscounts(finalLaborMRR + addonRevenue + itsoMRR, manualDiscounts);
  const finalMRR         = md.finalMRR;
  const effectiveMRR     = finalMRR + flexBlockMRR;

  // ── Cost + margin ──
  // engineCost mirrors the pre-v3.5.92.1 effectiveCost components EXCEPT
  // flexLaborCost — utility adds that uniformly across types.
  const engineCost = (Number(r.toolingCost)  || 0)
                   + (Number(r.svcCost)      || 0)
                   + (Number(r.addonCost)    || 0)
                   + (Number(r.includesCost) || 0)
                   + (Number(r.swapCost)     || 0)
                   + (Number(r.itsoCost)     || 0);
  const totalCost = engineCost + flexLaborCost;
  // Pre-v3.5.92.1 page used `: 0` when effectiveMRR was 0; utility uses
  // null (semantically more correct — can't compute a ratio at zero
  // revenue). QuotesPage line 169 (`totals.impliedGM || totals.gm || 0`)
  // and quoteDiff line ~131 (`?? totals.gm ?? 0`) both coerce null → 0
  // for display, so no consumer breaks. The edge case (Multi-Site quote
  // with zero locations and zero MRR) is not a real production state.
  const impliedGM = effectiveMRR > 0 ? 1 - totalCost / effectiveMRR : null;

  // ── NRC layer ──
  // Multi-Site onboarding is pure labor (per-user/workstation/server/
  // location fees from settings, line 369–377 of MultiSiteQuotePage.js
  // pre-v3.5.92.1). No hardware, no porting, no activation. Identity
  // nrc = onboarding + 0 + 0 + 0 holds trivially.
  //
  // NEW for Multi-Site in v3.5.92.1: effectiveOnboarding + discount/mode/
  // pct metadata. Pre-deploy MultiSite used OnboardingIncentive and
  // persisted the incentive at the top-level onboarding_incentive column
  // but never rolled the post-incentive number into totals — so every
  // consumer (proposal renderer, QuotesPage NRC column, Deal Summary)
  // had to redo the math from the raw column. This deploy lands those
  // fields in totals; consumers migrate to read them in v3.5.92.3.
  const onboarding   = Number(r.onboarding) || 0;
  const incentive    = resolveOnboardingIncentive(onboarding, obIncentive);
  const nrc          = onboarding;

  // ── Contract value (TCV) ──
  // Behavior change vs pre-v3.5.92.1: uses effectiveMRR (with flex) and
  // post-incentive labor. Pre-deploy MultiSite line 378 was
  // `finalMRR * contractTerm + onboarding` — excluded flex AND used the
  // raw fee. Customer-facing TCV in the proposal renderer should reflect
  // what the customer pays, and the customer pays the post-incentive
  // amount. Matches the IT behavior change locked in v3.5.92.0.
  const effectiveNRC  = incentive.effectiveOnboarding ?? onboarding;
  // v3.7.8: targeted one-time discounts. Multi-Site (like IT) has only the
  // onboarding bucket, based on the post-incentive figure.
  const otd           = resolveOneTimeDiscountFields(
    { onboarding: effectiveNRC, hardware: 0, activation: 0, porting: 0 },
    oneTimeDiscounts
  );
  const netNRC        = otd.netNRC;
  const contractValue = effectiveMRR * (Number(contractTerm) || 0) + netNRC;

  return {
    // ── Type discriminator ──
    quoteType: 'multisite',

    // ── Revenue ──
    finalMRR,
    effectiveMRR,
    contractValue,

    // ── Manual discounts (v3.5.99.7.23) ──
    ...md.fields,

    // ── NRC layer ──
    nrc,
    onboarding,
    effectiveOnboarding: incentive.effectiveOnboarding,
    hardwareNRC:   0,
    activationNRC: 0,
    portingNRC:    0,
    onboardingDiscountAmount: incentive.onboardingDiscountAmount,
    onboardingIncentiveMode:  incentive.onboardingIncentiveMode,
    onboardingIncentivePct:   incentive.onboardingIncentivePct,

    // ── One-time discounts (v3.7.8) ──
    netNRC,
    ...otd.fields,

    // ── Cost + margin ──
    totalCost,
    impliedGM,

    // ── ITSO (present on multisite per §5 #5 decision) ──
    // NEW: pre-v3.5.92.1 MultiSite computed these (line 339–344) but never
    // persisted them. Consumers that wanted to render the ITSO line on a
    // MultiSite proposal had to re-run getItsoUpliftRate(settings,
    // industryRisk) from inputs at render time. Now they read totals.
    itsoMRR,
    itsoLabel:      r.itsoLabel || null,
    itsoUpliftRate: Number(r.itsoUpliftRate) || 0,

    // ── Multi-Site bucket ──
    // Per locked spec — Multi-Site-specific fields live here rather than
    // at top level. Distinguishes from IT-engine internals (compMult /
    // discRate / discount) which are NOT applicable on MultiSite (the
    // page doesn't apply compliance/complexity multipliers per the
    // v3.5.58 architectural decision, and uses termDiscRate + multiDiscRate
    // as its own discount mechanism). termDiscRate and finalLaborMRR were
    // previously completely lost on save — they live here now so consumers
    // can reconstruct the discount chain without re-reading settings.
    multisite: {
      locationCount:    Number(r.locationCount)    || 0,
      multiDiscRate:    Number(r.multiDiscRate)    || 0,
      // v3.5.95.0 — margin-guarded discount engine. multiDiscRate (above)
      // remains the APPLIED rate per backward-compat (existing consumers
      // at proposal renderer / quoteDiff / QuoteHistory read multiDiscRate
      // and need the post-floor-cap value). The two new fields below
      // capture the audit trail: what the tier said vs whether the floor
      // reduced it. Used by MultiSiteQuotePage's three rep-UX surfaces
      // (pricing card / KPI strip / line-items row) to show the "8%
      // configured · 3% applied (GM floor)" state when capped is true.
      multiDiscRateConfigured: Number(r.multiDiscRateConfigured) || 0,
      multiDiscRateCapped:     Boolean(r.multiDiscRateCapped),
      termDiscRate:     Number(r.termDiscRate)     || 0,
      finalLaborMRR,
      addonRevenue,
      totalLocationMRR: Number(r.totalLocationMRR) || 0,
    },

    // ── Flex block (all-null when no block selected) ──
    ...flex,
  };
}

// ─── Voice (v3.5.92.2) ────────────────────────────────────────────────────
//
// Voice migration drives the §4a NRC-semantic split into action. calcVoice
// (extended in this same deploy) returns four discrete NRC subcategory
// totals: onboardingNrc (Programming & Configuration only — the labor-
// discount base), hardwareNrc (phones / BYOH / lease prepay), portingNrc
// (carrier per-DID fees), activationNrc (license upfronts + 10DLC). The
// utility just lifts those into the unified shape — no per-section line
// filtering at the consumer site anymore.
//
// Voice has no flex block (FlexTimeSelector isn't on this page) and no
// ITSO (Shaun #5: dead fields on voice — omit, not null). IT-engine
// internals are also absent.
//
// engineResult = calcVoice() return.

function buildVoiceTotals(result, context) {
  const { obIncentive, contractTerm, manualDiscounts, oneTimeDiscounts } = context;

  // ── Revenue ──
  // v3.5.99.7.23: manual discounts apply to the Voice contract MRR.
  const md           = resolveManualDiscounts(Number(result.finalMRR) || 0, manualDiscounts);
  const finalMRR     = md.finalMRR;
  const effectiveMRR = finalMRR;  // Voice has no flex block currently

  // ── Cost + margin ──
  // calcVoice returns totalCost directly (costMrr — there is no flex labor
  // to add on Voice). gm in the engine output is finalMRR > 0 ? 1 -
  // totalCost / finalMRR : 0; we recompute here using null at the edge
  // instead of 0 for the zero-revenue case (semantically correct).
  const totalCost = Number(result.totalCost) || 0;
  const impliedGM = effectiveMRR > 0 ? 1 - totalCost / effectiveMRR : null;

  // ── NRC layer (§4a split — Programming & Configuration only is discount-eligible) ──
  const onboardingNrc = Number(result.onboardingNrc) || 0;
  const hardwareNrc   = Number(result.hardwareNrc)   || 0;
  const portingNrc    = Number(result.portingNrc)    || 0;
  const activationNrc = Number(result.activationNrc) || 0;
  const nrc           = Number(result.nrc) || (onboardingNrc + hardwareNrc + portingNrc + activationNrc);

  // BEHAVIOR CHANGE vs pre-v3.5.92.2: the incentive base was previously
  // `laborNrc = result.nrc - hardwareNrc` (computed inline in
  // VoiceQuotePage.js line 376–379), which lumped Programming + Porting
  // + Activation. Per Shaun's §4a answer (May 10 2026), the picker
  // discounts ONLY Programming & Configuration. The VoiceQuotePage UI
  // sites that wire OnboardingIncentive's `fee` prop must also be updated
  // in this same deploy (otherwise the picker computes against the wrong
  // base and obIncentive.effectiveFee won't match this util's math).
  const incentive    = resolveOnboardingIncentive(onboardingNrc, obIncentive);

  // ── Contract value (TCV) ──
  // Customer-facing TCV = recurring × term + total upfront customer charge.
  // Total upfront = post-incentive Programming + all other NRC (unchanged
  // by incentive). Pre-v3.5.92.2 Voice didn't persist contractValue at all
  // (totals shape was only 5 fields); this is a net-new persisted field.
  // v3.7.8: targeted one-time discounts across all four NRC buckets. The
  // onboarding base is post-incentive; the others are the engine subcategory
  // totals. netNRC replaces effectiveTotalNRC in the TCV derivation.
  const otd = resolveOneTimeDiscountFields(
    {
      onboarding: incentive.effectiveOnboarding ?? onboardingNrc,
      hardware:   hardwareNrc,
      activation: activationNrc,
      porting:    portingNrc,
    },
    oneTimeDiscounts
  );
  const netNRC        = otd.netNRC;
  const contractValue = effectiveMRR * (Number(contractTerm) || 0) + netNRC;

  return {
    // ── Type discriminator ──
    quoteType: 'voice',

    // ── Revenue ──
    finalMRR,
    effectiveMRR,
    contractValue,

    // ── Manual discounts (v3.5.99.7.23) ──
    ...md.fields,

    // ── NRC layer ──
    nrc,
    onboarding:           onboardingNrc,       // labor-only (Programming & Config)
    effectiveOnboarding:  incentive.effectiveOnboarding,
    hardwareNRC:          hardwareNrc,
    activationNRC:        activationNrc,
    portingNRC:           portingNrc,
    onboardingDiscountAmount: incentive.onboardingDiscountAmount,
    onboardingIncentiveMode:  incentive.onboardingIncentiveMode,
    onboardingIncentivePct:   incentive.onboardingIncentivePct,

    // ── One-time discounts (v3.7.8) ──
    netNRC,
    ...otd.fields,

    // ── Cost + margin ──
    totalCost,
    impliedGM,

    // ── ITSO ABSENT (Shaun #5 — don't pollute Voice with dead fields) ──
    // ── IT-engine internals ABSENT ──
    // ── Flex block ABSENT (Voice has no FlexTimeSelector currently) ──

    // ── Voice bucket ──
    voice: {
      estTax:              Number(result.estTax)         || 0,
      bundleVoiceDiscount: Number(result.bundleDiscount) || 0,
    },
  };
}

// ─── Bundle (v3.5.92.2) ───────────────────────────────────────────────────
//
// Bundle composes IT + Voice. The page passes both engine results plus the
// Bundle-level derivations (combined MRR/cost, free-phones flag, bundle
// voice discount $). Utility produces the unified shape with the bundle:
// {...} bucket holding the renamed Bundle-internal fields (§7 #7 decision
// — bundleItFinalMRR / bundleVoiceFinalMRR rather than the ambiguous
// itMRR / voiceMRR).
//
// Per §4a, the onboarding-incentive picker base on Bundle is
// `itResult.onboarding + voiceResult.onboardingNrc` (IT labor + Voice
// Programming & Configuration), NOT the pre-v3.5.92.2 `combinedNRC`
// (which included voice hardware + porting + activation). This is the
// largest customer-facing behavior change of the v3.5.92.x arc — Bundle
// quotes with an active incentive now discount a smaller base. Reps
// confirming Bundle pricing with customers between v3.5.92.2 deploy and
// the next save should re-verify the effective NRC figure.
//
// engineResult = {
//   itResult,                — calcQuote() return
//   voiceResult,             — calcVoice() return (with freePhones rebuild applied; "voiceResultFinal" in the page)
//   itBaseMRR,               — IT base package MRR (pre-add-ons, pre-uplifts) — drives freePhones threshold
//   bundleVoiceDiscount,     — $ amount of voice MRR discount from calcBundleDiscount()
//   combinedMRR,             — itResult.finalMRR + (voice.finalMRR - bundleVoiceDiscount)
//   combinedCost,            — itResult.totalCost + voiceResult.totalCost (pre-flex)
//   freePhones,              — boolean
// }

function buildBundleTotals(r, context) {
  const { obIncentive, flexBlock, contractTerm, settings, manualDiscounts, oneTimeDiscounts } = context;
  const it    = r.itResult    || {};
  const voice = r.voiceResult || {};

  // ── Flex block (lives on IT side of Bundle — FlexTimeSelector is IT-flavored) ──
  const burdenedRate  = getBurdenedRate(settings);
  const flex          = resolveFlexBlock(flexBlock, burdenedRate);
  const flexBlockMRR  = flex.flexBlockMRR  || 0;
  const flexLaborCost = flex.flexLaborCost || 0;

  // ── Revenue ──
  // v3.5.99.7.23: manual discounts apply on top of the combined (post-bundle-
  // discount) MRR.
  const md           = resolveManualDiscounts(Number(r.combinedMRR) || 0, manualDiscounts);
  const finalMRR     = md.finalMRR;
  const effectiveMRR = finalMRR + flexBlockMRR;

  // ── Cost + margin ──
  const totalCost = (Number(r.combinedCost) || 0) + flexLaborCost;
  const impliedGM = effectiveMRR > 0 ? 1 - totalCost / effectiveMRR : null;

  // ── NRC layer (§4a — composed split) ──
  // IT contributes only onboarding labor (no hardware/porting/activation on IT).
  // Voice contributes the full subcategory split from calcVoice.
  const itOnboarding   = Number(it.onboarding)        || 0;  // IT labor (Programming & Config equivalent on IT)
  const voiceOnboard   = Number(voice.onboardingNrc)  || 0;  // Voice Programming & Config
  const voiceHardware  = Number(voice.hardwareNrc)    || 0;
  const voicePorting   = Number(voice.portingNrc)     || 0;
  const voiceActivation= Number(voice.activationNrc)  || 0;

  // Picker base = IT labor + Voice Programming. NOT voice hardware/porting/
  // activation. This is the §4a behavior change.
  const onboardingLabor = itOnboarding + voiceOnboard;
  const incentive       = resolveOnboardingIncentive(onboardingLabor, obIncentive);

  const nrc = onboardingLabor + voiceHardware + voicePorting + voiceActivation;

  // ── Contract value (TCV) ──
  // v3.7.8: targeted one-time discounts. Onboarding base = post-incentive
  // composed labor (IT onboarding + Voice Programming); hardware/activation/
  // porting come from the Voice side. netNRC replaces effectiveTotalNRC in TCV.
  const otd = resolveOneTimeDiscountFields(
    {
      onboarding: incentive.effectiveOnboarding ?? onboardingLabor,
      hardware:   voiceHardware,
      activation: voiceActivation,
      porting:    voicePorting,
    },
    oneTimeDiscounts
  );
  const netNRC        = otd.netNRC;
  const contractValue = effectiveMRR * (Number(contractTerm) || 0) + netNRC;

  // ── Voice MRR piece (post bundle discount) ──
  // calcBundleDiscount returns voiceDiscount as a $ amount applied to voice
  // MRR; page-side voiceDiscountedMRR = voiceResult.finalMRR - bundleDiscount.
  // We reconstruct here so the bucket reads cleanly without depending on
  // the page passing a pre-derived value.
  const voiceFinalPre  = Number(voice.finalMRR)     || 0;
  const bundleVoiceDisc= Number(r.bundleVoiceDiscount) || 0;
  const voiceFinalPost = Math.max(0, voiceFinalPre - bundleVoiceDisc);

  return {
    quoteType: 'bundle',

    finalMRR,
    effectiveMRR,
    contractValue,

    // ── Manual discounts (v3.5.99.7.23) ──
    ...md.fields,

    nrc,
    onboarding:          onboardingLabor,    // composed labor — IT onboarding + Voice Programming
    effectiveOnboarding: incentive.effectiveOnboarding,
    hardwareNRC:         voiceHardware,
    activationNRC:       voiceActivation,
    portingNRC:          voicePorting,
    onboardingDiscountAmount: incentive.onboardingDiscountAmount,
    onboardingIncentiveMode:  incentive.onboardingIncentiveMode,
    onboardingIncentivePct:   incentive.onboardingIncentivePct,

    // ── One-time discounts (v3.7.8) ──
    netNRC,
    ...otd.fields,

    totalCost,
    impliedGM,

    // ── ITSO (Bundle inherits from IT side per §5 #5) ──
    itsoMRR:        Number(it.itsoMRR)        || 0,
    itsoLabel:      it.itsoLabel || null,
    itsoUpliftRate: Number(it.itsoUpliftRate) || 0,

    // ── IT-engine internals (Bundle inherits IT's discount chain) ──
    itSubtotal: Number(it.itSubtotal) || 0,
    compMult:   Number(it.compMult)   || 1,
    discRate:   Number(it.discRate)   || 0,
    discount:   Number(it.discount)   || 0,  // NEGATIVE

    // ── Bundle bucket (renamed per §7 #7 May 10 2026) ──
    bundle: {
      bundleItFinalMRR:    Number(it.finalMRR) || 0,    // was: itMRR
      bundleVoiceFinalMRR: voiceFinalPost,              // was: voiceMRR (post bundle discount)
      bundleVoiceDiscount: bundleVoiceDisc,
      freePhones:          !!r.freePhones,
    },

    // ── Flex block (all-null when no block selected) ──
    ...flex,
  };
}

// ─── FlexIT (v3.5.92.2) ───────────────────────────────────────────────────
//
// FlexIT is the simplest of the five. No recurring MRR (it's a labor-hour
// prepayment model, not a contract), no ITSO, no onboarding incentive
// (Shaun: "people buying time know they're buying time" — see CLAUDE.md
// §10a #6). The "flex block" on FlexIT is the upfront fee itself rather
// than a recurring layer like on IT/Multi-Site/Bundle, so it doesn't
// contribute to flexBlockMRR — but the block detail fields still populate
// for the proposal renderer (which reads totals.flexBlockPrice et al).
//
// nrc = flexitRevenue (prepayAmount when no block selected, or
// flexBlock.blockPrice when one is). onboarding = 0 — no setup labor on
// FlexIT; the prepayment is for FUTURE T&M hours, not for one-time setup.
//
// engineResult = {
//   prepayAmount, prepayHours,
//   flexBlock,            — calcFlexBlock() return or null
//   flexitRevenue,        — page-derived: hasFlexBlock ? blockPrice : prepayAmount
//   flexitLaborHours,
//   flexitLaborCost,
//   flexitImpliedGM,
//   remoteRate,
//   burdenedHourlyRate,
// }

function buildFlexITTotals(r, context) {
  const flexBlock = r.flexBlock || null;

  // FlexIT-specific flex block handling — block IS the upfront fee, not
  // a recurring MRR layer. flexBlockMRR stays 0; the block detail fields
  // populate for proposal renderer back-compat (buildSignatureDocument.js
  // line 174–183 reads totals.flexBlockPrice et al).
  const flexFields = flexBlock ? {
    flexBlockHours:     Number(flexBlock.hours)     || 0,
    flexBlockMRR:       0,                           // one-time, not MRR
    flexBlockPrice:     Number(flexBlock.blockPrice) || 0,
    flexBlockFullPrice: Number(flexBlock.fullPrice)  || 0,
    flexBlockSavings:   Number(flexBlock.savings)    || 0,
    flexBlockDiscount:  flexBlock.discount != null ? Number(flexBlock.discount) : null,
    flexLaborCost:      Number(r.flexitLaborCost)    || 0,  // total labor cost (block hours × burden)
  } : {
    flexBlockHours: null, flexBlockMRR: null, flexBlockPrice: null,
    flexBlockFullPrice: null, flexBlockSavings: null, flexBlockDiscount: null,
    flexLaborCost: null,
  };

  const nrc = Number(r.flexitRevenue) || 0;
  // FlexIT has no recurring contract MRR. contractValue == upfront charge.
  const contractValue = nrc;

  return {
    quoteType: 'flexit',

    finalMRR:     0,        // FlexIT has no recurring MRR (literal 0, not absent — consumers expect this field)
    effectiveMRR: 0,
    contractValue,

    nrc,
    onboarding:          0,    // FlexIT prepayment is for future T&M hours, NOT one-time setup labor
    effectiveOnboarding: null,
    hardwareNRC:   0,
    activationNRC: 0,
    portingNRC:    0,
    onboardingDiscountAmount: 0,
    onboardingIncentiveMode:  null,
    onboardingIncentivePct:   null,

    totalCost:    Number(r.flexitLaborCost)  || 0,
    impliedGM:    r.flexitImpliedGM != null ? Number(r.flexitImpliedGM) : null,

    // ── ITSO ABSENT (Shaun #5) ──
    // ── IT-engine internals ABSENT ──

    // ── FlexIT bucket ──
    flexit: {
      prepayAmount:       Number(r.prepayAmount)       || 0,
      prepayHours:        Number(r.prepayHours)        || 0,
      remoteRate:         Number(r.remoteRate)         || 0,
      burdenedHourlyRate: Number(r.burdenedHourlyRate) || 0,
      flexitLaborHours:   Number(r.flexitLaborHours)   || 0,
    },

    // ── Flex block (the block IS the upfront fee for FlexIT) ──
    ...flexFields,
  };
}
